import { motion } from "motion/react";
import { Sparkles } from "lucide-react";
import type { SlideProps } from "@/engine/types";
import { Badge, GradientText, Reveal, SlideShell, useAccent } from "@/ui";
import { TypingSearchBar } from "./components/TypingSearchBar";

function PulsingSparkle({
  active,
  className,
  stop,
  size,
  delay,
}: {
  active: boolean;
  className: string;
  /** Which end of the chapter accent gradient to tint with. */
  stop: "from" | "to";
  size: string;
  delay: number;
}) {
  const [from, to] = useAccent();
  const color = stop === "from" ? from : to;
  return (
    <motion.span
      className={`pointer-events-none absolute ${className}`}
      animate={
        active
          ? { opacity: [0.2, 0.85, 0.2], scale: [0.85, 1.1, 0.85] }
          : { opacity: 0.25, scale: 1 }
      }
      transition={
        active
          ? { duration: 3.4, repeat: Infinity, ease: "easeInOut", delay }
          : { duration: 0.3 }
      }
      aria-hidden
    >
      <Sparkles className={size} style={{ color }} />
    </motion.span>
  );
}

export function CloseSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={7} kicker="" align="center">
      <div className="relative mx-auto flex w-full max-w-5xl flex-col items-center gap-12">
        <PulsingSparkle
          active={active}
          className="-top-16 right-10"
          stop="from"
          size="h-9 w-9"
          delay={0}
        />
        <PulsingSparkle
          active={active}
          className="top-44 -left-14"
          stop="to"
          size="h-7 w-7"
          delay={1.1}
        />
        <PulsingSparkle
          active={active}
          className="-right-16 bottom-8"
          stop="from"
          size="h-6 w-6"
          delay={2.2}
        />

        <Reveal active={active}>
          <Badge>End of SEO — Beginner</Badge>
        </Reveal>

        <Reveal active={active} delay={0.1} className="w-full max-w-2xl">
          <TypingSearchBar
            active={active}
            query="seo — intermediate?"
            className="w-full"
          />
        </Reveal>

        <Reveal active={active} delay={0.25}>
          <h1 className="text-gradient pb-3 font-display text-8xl font-semibold tracking-tight">
            Thank you
          </h1>
        </Reveal>

        <Reveal active={active} delay={0.4}>
          <div className="flex max-w-4xl flex-col gap-5">
            <p className="text-3xl leading-relaxed text-white/85">
              SEO is a long-horizon discipline:{" "}
              <GradientText className="font-semibold">
                small credible wins compound
              </GradientText>{" "}
              if you keep showing up.
            </p>
          </div>
        </Reveal>
      </div>
    </SlideShell>
  );
}
